import {
  Component,
  Event,
  EventEmitter,
  Prop,
  State,
  Watch,
  h,
  ComponentInterface,
  Method,
  getMode,
  Element,
  Fragment
} from '@stencil/core';
import {
  jsonWatcher,
  emitClickTrackerEvent,
  emitCustomEvent,
  ActivityMapRegion,
  onFocusWithTab
} from '../../utils/utils';
import { Profile } from './components/ds-contextual-navigation-profile/ds-contextual-navigation-profile';
import {
  CountrySelectorConfig,
  Country
} from './components/ds-contextual-navigation-country-selector/ds-contextual-navigation-country-selector';
import clsx from 'clsx';
import { ShoppingCartConfig } from './components/ds-header-shopping-cart/ds-header-shopping-cart';
import { NotificationBellConfig } from './components/ds-header-notification-bell/ds-header-notification-bell';
import { Labels, setTranslations, translate, Translations } from '../../utils/translations';
import { ContactUsConfig } from './components/ds-contextual-navigation-mobile/ds-header-mobile-contact-us/ds-header-mobile-contact-us';
import store, { Store } from '../../store/store';

export interface Link {
  title: string;
  url?: string;
  externalLink?: boolean;
  id?: string;
}

export interface LandingZone extends Link {}

export interface CustomLogo {
  imgUrl: string;
  imgAlt: string;
}

export interface MainNavigation extends Link {
  /**
   * @maxItems 2
   */
  subMenuRows?: SubMenuRow[];
  deepLink?: Link;
}

export interface ExploreSapLink {
  title: string;
  displayTitle?: string;
  url?: string;
  /**
   * @maxItems 2
   */
  subMenuRows: SubMenuRow[];
}

export interface SubMenuRow {
  /**
   * @maxItems 4
   */
  subMenuLinkItems: LinkItem[];
}

export interface LinkItem {
  category?: string;
  url?: string;
  displayItemsAsCategoryLink?: boolean;
  items?: Array<Link & { items?: Link[] }>;
}

export interface SearchConfig {
  url: string;
  placeholder?: string;
  inputName?: string;
  disabled?: boolean;
  realTimeSearch?: boolean;
  queryParameters?: { [key: string]: string | boolean };
}

export interface User {
  name: string;
  username: string;
  email: string;
  avatarUrl?: string;
}

export interface Notifications {
  profile: ProfileNotifications;
}

export interface ProfileNotifications {
  [key: string]: {
    count: number;
    notifications?: Notification[];
    limit?: number;
  };
}

export interface Notification {
  title: string;
  description?: string;
}

@Component({
  tag: 'ds-contextual-navigation',
  styleUrls: {
    default: 'ds-contextual-navigation.dark.scss',
    light: 'ds-contextual-navigation.light.scss'
  },
  shadow: true
})
export class ContextualNavigation implements ComponentInterface {
  @Element() element: HTMLDsContextualNavigationElement;

  /**
   * The main navigation links. Submenus can be nested inside of links.
   */
  @Prop() mainLinks?: MainNavigation[] | string = [];
  private _mainLinks: MainNavigation[];

  @Prop() exploreSapLink: ExploreSapLink | string;
  private _exploreSapLink: ExploreSapLink;

  @Prop() hideExploreSapLink: boolean = false;

  @Prop() homeLogoUrl?: string = 'https://sap.com';

  @Prop() landingZone: LandingZone | string;
  private _landingZone: LandingZone;

  @Prop() customLogo: CustomLogo | string;
  private _customLogo: CustomLogo;

  @Prop() profile?: Profile | string;
  private _profile: Profile;

  @Prop() searchConfig?: SearchConfig | string = { disabled: true, url: '' };
  private _searchConfig: SearchConfig;

  @Prop() shoppingCartConfig?: ShoppingCartConfig | string;
  private _shoppingCartConfig: ShoppingCartConfig;

  @Prop() countrySelectorConfig?: CountrySelectorConfig | string;
  private _countrySelectorConfig: CountrySelectorConfig;

  @Prop() contactUsConfig?: ContactUsConfig | string;
  private _contactUsConfig: ContactUsConfig;

  @Prop() loggedInUser: User | string;
  private _loggedInUser: User;

  @Prop() lastVisitedCountry: Country | string;
  private _lastVisitedCountry: Country;

  /** Deprecated: please use loggedInUser.avatarUrl instead */
  @Prop() avatarSourceUrl?: string = 'https://avatars.profile.sap.com';

  @Prop() notifications: Notifications | string;
  private _notifications: Notifications;

  @Prop() notificationBellConfig?: NotificationBellConfig | string;
  private _notificationBellConfig: NotificationBellConfig;

  @Prop() disableProfileMenu: boolean = false;

  @Prop() showSearchInputByDefault?: boolean = false;

  @Prop() translations: Translations | string;
  private _translations: Translations;

  @Prop() mobileNavigationContext?: string[] | string;

  @Prop() mode?: 'light' | 'default';

  /**
   * Used to be able to get component version on a consumer page.
   */
  @Prop() version: string = process.env.npm_package_version;
  private _mobileNavigationContext: string[];

  private mainNav!: HTMLElement;
  private mainNavUl!: HTMLElement;
  private mainNavUlWidth: number;
  private searchInputWrapper!: HTMLElement;
  private headerWrapper!: HTMLElement;
  private isNavItemsEmpty!: boolean;
  private isActionsEmpty!: boolean;
  private onResize: () => void;

  @State() searchInputShown: boolean = this.showSearchInputByDefault;
  @State() isSearchButtonFocused: boolean = false;
  @State() mobileHeaderShown: boolean;
  @State() boundaryLeft: number;
  @State() boundaryRight: number;
  @State() windowWidth: number;
  @State() headerMargin: number;
  @State() styleLoaded: boolean = false;

  @Event({ composed: true, bubbles: true }) countrySelected: EventEmitter;
  @Event({ composed: true, bubbles: true }) login: EventEmitter;
  @Event({ composed: true, bubbles: true }) logout: EventEmitter;
  @Event({ composed: true, bubbles: true }) cxsMobileMenuOpened: EventEmitter;
  @Event({ composed: true, bubbles: true }) cxsMobileMenuClosed: EventEmitter;

  @Method()
  async changeState(change: (store: Store) => void) {
    change(store);
  }

  private _handleWindowResize() {
    if (!this.styleLoaded || this.windowWidth === window.innerWidth) {
      // if style is not loaded or
      // there's no change in window width (to check if this resize event was triggered by mobile scroll)
      // break the function
      return;
    }
    this.windowWidth = window.innerWidth;
    this.mainNavUlWidth = this.mainNavUl?.offsetWidth ?? 0;
    const mainNavWidth = this.mainNav?.clientWidth || this.searchInputWrapper?.clientWidth;
    const mobileMenuBreakpoint = parseInt(
      getComputedStyle(this.headerWrapper).getPropertyValue('--cxs-screen-lg-min')
    );
    // Determine if the view is mobile
    const isMobileView =
      !mainNavWidth || // Fallback to mobile view if `mainNavWidth` is unavailable
      this.mainNavUlWidth + 1 >= mainNavWidth ||
      this.windowWidth < mobileMenuBreakpoint;
    // if the view has been changed
    if (store.isMobileView !== isMobileView) {
      store.isMobileView = isMobileView;
      // Hide the search input in mobile view
      if (isMobileView) {
        this.searchInputShown = false;
      }
      // Emit custom event only if the view has changed
      emitCustomEvent('cxsdsHeaderUpdatedBreakpoint', {
        breakpoint: isMobileView ? 'mobile' : 'desktop'
      });
    }

    this.calculateSubMenusPositions();

    this._showMobileHeader(false);
  }

  private calculateSubMenusPositions() {
    // these state variables are needed only to calculate submenus positions under mainLinks
    if (store?.mainLinks && store?.mainLinks?.length !== 0) {
      const parentElem = this.headerWrapper?.getBoundingClientRect();

      this.headerMargin = parseInt(
        getComputedStyle(this.headerWrapper).getPropertyValue('margin-inline-start')
      );
      this.boundaryLeft = parentElem?.left;
      this.boundaryRight = parentElem?.right;
    }
  }

  private _propWatcher<T>(prop: string, value: T): void {
    const resolved = jsonWatcher(prop, value);
    if (resolved) {
      this[`_${resolved.prop}`] = resolved.value;
    }
  }

  private _showSearchInput(show: boolean = false) {
    this.searchInputShown = show;
  }

  private _showMobileHeader(show: boolean = false) {
    document.documentElement.style.setProperty('--mobile-menu-height', `${window.innerHeight}px`);
    const hasToggled = show !== this.mobileHeaderShown;
    this.mobileHeaderShown = show;
    if (hasToggled && show) {
      this.cxsMobileMenuOpened.emit();
    } else if (hasToggled) {
      this.cxsMobileMenuClosed.emit();
    }
  }

  componentWillLoad(): void {
    this._updateProps();
  }

  componentDidLoad(): void {
    this.isNavItemsEmpty =
      (this.hideExploreSapLink || !this.exploreSapLink) &&
      this._mainLinks?.every((link) => link.subMenuRows?.length === 0 && !link.url);
    this.isActionsEmpty =
      !this._countrySelectorConfig?.enabled &&
      !this._shoppingCartConfig?.enabled &&
      !this._notificationBellConfig?.enabled &&
      this.disableProfileMenu &&
      store.searchConfig?.disabled;
    // interval is needed to prevent un-styled content flash in FF for dist-custom-elements build.
    // check https://github.com/ionic-team/stencil/issues/3437 if it is fixed on stencil's side
    const wait = setInterval(() => {
      if (this.headerWrapper) {
        clearInterval(wait);
        // when defined style in contextual-navigation.scss is finally loaded, set visibility to visible
        this.headerWrapper.style.visibility = 'visible';
        this.styleLoaded = true;
        this.windowWidth = null;

        this._handleWindowResize();

        emitCustomEvent('cxsdsHeaderComponentReady');
      }
    }, 10);

    let resizeTimeout;
    this.onResize = () => {
      if (!resizeTimeout) {
        const that = this;

        // throttle instead of debounce to avoid mixed mobile/desktop view during resize
        resizeTimeout = setTimeout(function () {
          resizeTimeout = null;
          that._handleWindowResize();
        }, 100);
      }
    };

    window.addEventListener('resize', this.onResize);
    window.addEventListener('beforeunload', () => this._showMobileHeader(false));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  // for case, when mainLinks are initially empty and are filled in only after componentDidLoad has already been called
  componentDidUpdate(): void {
    this.calculateSubMenusPositions();
  }

  @Watch('mainLinks')
  @Watch('exploreSapLink')
  @Watch('hideExploreSapLink')
  @Watch('shoppingCartConfig')
  @Watch('landingZone')
  @Watch('profile')
  @Watch('searchConfig')
  @Watch('countrySelectorConfig')
  @Watch('contactUsConfig')
  @Watch('loggedInUser')
  @Watch('lastVisitedCountry')
  @Watch('notifications')
  @Watch('notificationBellConfig')
  @Watch('translations')
  @Watch('mobileNavigationContext')
  private _updateProps(): void {
    this._propWatcher('mainLinks', this.mainLinks);
    this._propWatcher('exploreSapLink', this.exploreSapLink);
    this._propWatcher('hideExploreSapLink', this.hideExploreSapLink);
    this._propWatcher('shoppingCartConfig', this.shoppingCartConfig);
    this._propWatcher('landingZone', this.landingZone);
    this._propWatcher('profile', this.profile);
    this._propWatcher('searchConfig', this.searchConfig);
    this._propWatcher('countrySelectorConfig', this.countrySelectorConfig);
    this._propWatcher('contactUsConfig', this.contactUsConfig);
    this._propWatcher('loggedInUser', this.loggedInUser);
    this._propWatcher('lastVisitedCountry', this.lastVisitedCountry);
    this._propWatcher('notifications', this.notifications);
    this._propWatcher('notificationBellConfig', this.notificationBellConfig);
    this._propWatcher('translations', this.translations);
    this._propWatcher('mobileNavigationContext', this.mobileNavigationContext);
    this._propWatcher('customLogo', this.customLogo);
    setTranslations(this._translations);
    store.searchConfig = this._searchConfig;
    store.mainLinks = this._mainLinks;
  }

  render() {
    return (
      <header
        class={clsx('contextual-navigation', {
          'contextual-navigation--search-header':
            !store.searchConfig.disabled && this.showSearchInputByDefault,
          'contextual-navigation--search-shown':
            this.searchInputShown && !this.showSearchInputByDefault,
          'contextual-navigation--mobile-shown': this.mobileHeaderShown
        })}
        data-activity-map-region='header'
        data-mode={getMode(this)}
      >
        <div
          class='wrapper'
          ref={(el) => (this.headerWrapper = el as HTMLElement)}
          style={{ visibility: 'hidden' }}
        >
          <div class='landing-zone'>
            <ds-link
              class={clsx('sap-logo', { 'sap-logo--custom': this._customLogo?.imgUrl })}
              href={this.homeLogoUrl}
              onClick={() =>
                emitClickTrackerEvent(
                  {
                    componentName: ActivityMapRegion.Header,
                    title: translate(Labels['LandingZone.SapLogo']),
                    url: this.homeLogoUrl
                  },
                  { hasUiActions: false }
                )
              }
              label={translate(Labels['LandingZone.SapLogo'])}
              linkTitle={translate(Labels['LandingZone.SapLogo'])}
            >
              {this._customLogo?.imgUrl ? (
                <img
                  part='custom-logo'
                  class='sap-log__img'
                  src={this._customLogo.imgUrl}
                  alt={this._customLogo.imgAlt}
                />
              ) : null}
            </ds-link>
            {this._landingZone ? (
              this._landingZone.url ? (
                <ds-link
                  href={this._landingZone.url}
                  onClick={() =>
                    emitClickTrackerEvent(
                      {
                        componentName: ActivityMapRegion.Header,
                        title: `Header Title: ${this._landingZone.title}`,
                        url: this._landingZone.url
                      },
                      { hasUiActions: false }
                    )
                  }
                  class='home-menu-item'
                  type='inherit'
                >
                  <span>{this._landingZone.title}</span>
                </ds-link>
              ) : (
                <p class='home-menu-item'>{this._landingZone.title}</p>
              )
            ) : null}
          </div>
          {!store.searchConfig?.disabled &&
          (this.searchInputShown || (this.showSearchInputByDefault && !store.isMobileView)) ? (
            <div
              class='search-input-wrapper'
              ref={(el) => (this.searchInputWrapper = el as HTMLElement)}
            >
              <ds-search-input
                class='search-input'
                url={store.searchConfig?.url}
                placeholder={store.searchConfig?.placeholder}
                inputName={store.searchConfig?.inputName}
                queryParameters={store.searchConfig?.queryParameters}
                realTimeSearch={store.searchConfig?.realTimeSearch}
                onKeyDown={(e) => {
                  if (e.code === 'Escape') {
                    this._showSearchInput(false);
                    this.element.shadowRoot
                      .querySelector('.search-button')
                      .shadowRoot.querySelector('button')
                      .focus();
                    this.isSearchButtonFocused = true;
                    emitClickTrackerEvent({
                      componentName: ActivityMapRegion.Header,
                      title: `Close Search`,
                      url: store.searchConfig?.url
                    });
                  }
                }}
                onSearchInputCleared={(query) => {
                  emitClickTrackerEvent({
                    componentName: ActivityMapRegion.Header,
                    title: `Clear Search: "${query.detail}"`,
                    url: store.searchConfig?.url
                  });
                }}
                onSearchQuerySubmitted={(query) => {
                  emitClickTrackerEvent({
                    componentName: ActivityMapRegion.Header,
                    title: `Submit Search: "${query.detail}"`,
                    url: store.searchConfig?.url
                  });
                }}
                allowEmptySearchSubmit
                autoFocus
                data-testid='cxs-header-search-input'
              >
                {!store.isMobileView ? (
                  <Fragment>
                    <slot name='search-title' slot='search-title'></slot>
                    <slot name='search-results' slot='search-results'></slot>
                  </Fragment>
                ) : null}
              </ds-search-input>
            </div>
          ) : (
            <nav
              aria-label={translate(Labels['Navigation.MainLinks'])}
              class={clsx('main-navigation', { hidden: this.mobileHeaderShown })}
            >
              <div class='main-links-wrapper' ref={(el) => (this.mainNav = el as HTMLElement)}>
                {store.mainLinks?.length ? (
                  <ul
                    class={clsx({ hidden: store.isMobileView })}
                    aria-label={translate(Labels['Navigation.MainLinks'])}
                    ref={(el) => (this.mainNavUl = el as HTMLElement)}
                  >
                    {store.mainLinks?.map((link) => (
                      <li>
                        <ds-contextual-navigation-submenu
                          class='main-navigation__main-links'
                          name={link.title}
                          url={link.url}
                          items={link.subMenuRows}
                          deepLink={link.deepLink}
                          boundaryLeft={this.boundaryLeft}
                          boundaryRight={this.boundaryRight}
                          boundaryPadding={20}
                          headerMargin={this.headerMargin}
                        />
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div
                class={clsx('border', {
                  'border--hidden':
                    !this._exploreSapLink ||
                    this.hideExploreSapLink ||
                    !store.mainLinks?.length ||
                    store.isMobileView
                })}
              />
              {this._exploreSapLink && !this.hideExploreSapLink ? (
                <ds-contextual-navigation-submenu
                  class={clsx('main-navigation__explore-sap', {
                    'main-navigation__explore-sap--hidden': store.isMobileView
                  })}
                  name={this._exploreSapLink.title}
                  displayTitle={this._exploreSapLink.displayTitle}
                  items={this._exploreSapLink.subMenuRows}
                  exploreMenu
                />
              ) : null}
            </nav>
          )}
          <div
            class={clsx(
              'actions',
              { 'actions--hidden': this.searchInputShown && !this.showSearchInputByDefault },
              { hidden: (this.isActionsEmpty && this.isNavItemsEmpty) || store.isMobileView },
              {
                'actions--no-nav-items':
                  this.isNavItemsEmpty || (this.showSearchInputByDefault && this.hideExploreSapLink)
              },
              { 'actions--search-header': this.showSearchInputByDefault },
              { 'actions--empty': this.isActionsEmpty && !store.isMobileView }
            )}
          >
            {this.showSearchInputByDefault && this._exploreSapLink && !this.hideExploreSapLink ? (
              <nav
                aria-label={translate(Labels['Navigation.MainLinks'])}
                class={clsx('main-navigation', { hidden: this.mobileHeaderShown })}
              >
                <div class='main-links-wrapper'>
                  <ul
                    aria-label={translate(Labels['Navigation.MainLinks'])}
                    class={clsx({ hidden: store.isMobileView })}
                  >
                    <li>
                      <ds-contextual-navigation-submenu
                        class='main-navigation__explore-sap'
                        name={this._exploreSapLink.title}
                        displayTitle={this._exploreSapLink.displayTitle}
                        items={this._exploreSapLink.subMenuRows}
                        exploreMenu
                      />
                    </li>
                  </ul>
                </div>
              </nav>
            ) : null}
            {!store.searchConfig?.disabled && !this.showSearchInputByDefault && (
              <ds-navigation-button
                class={{
                  'search-button': true,
                  'search-button--focused': this.isSearchButtonFocused
                }}
                color='icon'
                title={
                  this.searchInputShown
                    ? translate(Labels['Search.Close'])
                    : translate(Labels['Search.Open'])
                }
                label={
                  this.searchInputShown
                    ? translate(Labels['Search.Close'])
                    : translate(Labels['Search.Open'])
                }
                onFocus={onFocusWithTab(() => (this.isSearchButtonFocused = true))}
                onFocusout={() => (this.isSearchButtonFocused = false)}
                onClick={() => {
                  this._showSearchInput(!this.searchInputShown);
                  emitClickTrackerEvent({
                    componentName: ActivityMapRegion.Header,
                    title: `${this.searchInputShown ? 'Open' : 'Close'} Search`,
                    url: store.searchConfig.url
                  });
                }}
              >
                {this.searchInputShown ? (
                  <ds-icon name='Decline' class='header-icon' size='big' />
                ) : (
                  <ds-icon name='Search' class='header-icon' size='big' />
                )}
              </ds-navigation-button>
            )}
            {this._shoppingCartConfig?.enabled && (
              <ds-header-shopping-cart
                url={this._shoppingCartConfig?.url}
                internalLink={!this._shoppingCartConfig?.externalLink}
                isFull={this._shoppingCartConfig?.isFull}
                hasUnreadNotifications={this._shoppingCartConfig?.hasUnreadNotifications}
              />
            )}
            {this._notificationBellConfig?.enabled && (
              <ds-header-notification-bell {...this._notificationBellConfig} />
            )}
            {!this.disableProfileMenu && (
              <ds-contextual-navigation-profile
                profile={this._profile}
                loggedInUser={this._loggedInUser}
                notifications={this._notifications?.profile}
                onLoginButtonClick={() => {
                  this.login.emit();
                }}
                onLogoutButtonClick={() => this.logout.emit()}
              >
                <slot name='profile-message' slot='profile-message'></slot>
              </ds-contextual-navigation-profile>
            )}
            {this._countrySelectorConfig?.enabled && (
              <ds-contextual-navigation-country-selector
                regions={this._countrySelectorConfig?.regions}
                searchDisabled={this._countrySelectorConfig?.searchDisabled}
                onCountrySelected={(country) => {
                  console.warn("@Event 'onCountrySelected' emitted! ", country);
                  this.countrySelected.emit(country);
                }}
                lastVisitedCountry={this._lastVisitedCountry}
                defaultActiveCountry={this._countrySelectorConfig?.defaultActiveCountry}
              />
            )}
          </div>
          <ds-contextual-navigation-mobile-masthead
            class={clsx({ hidden: !store.isMobileView })}
            profile={this._profile}
            loggedInUser={this._loggedInUser}
            mobileHeaderShown={this.mobileHeaderShown}
            onOpenButtonClick={() => this._showMobileHeader(true)}
            onCloseButtonClick={() => this._showMobileHeader(false)}
            exploreSapLink={this._exploreSapLink}
            hideExploreSapLink={this.hideExploreSapLink}
            shoppingCartConfig={this._shoppingCartConfig}
            countrySelectorConfig={this._countrySelectorConfig}
            contactUsConfig={this._contactUsConfig}
            notifications={this._notifications?.profile}
            notificationBellConfig={this._notificationBellConfig}
            disableProfileMenu={this.disableProfileMenu}
            onLoginButtonClick={() => {
              this.login.emit();
            }}
            onLogoutButtonClick={() => {
              this.logout.emit();
              this._showMobileHeader(false);
            }}
            lastVisitedCountry={this._lastVisitedCountry}
            onCountrySelected={(country) => {
              console.warn("@Event 'onCountrySelected' emitted! ", country);
              this.countrySelected.emit(country);
            }}
            navigationContext={this._mobileNavigationContext}
          >
            {store.isMobileView ? (
              <Fragment>
                <slot name='search-title' slot='search-title'></slot>
                <slot name='search-results' slot='search-results'></slot>
              </Fragment>
            ) : null}
            <slot name='contact-us' slot='contact-us'></slot>
          </ds-contextual-navigation-mobile-masthead>
        </div>
      </header>
    );
  }
}
